// Firebase Realtime Database writer for guideline PDF lead capture.
//
// Deduped by phone number: the phone (digits only) IS the record key, so a
// repeat opt-in for a different subject merges into one lead and keeps every
// subject tag as a child flag (decision in GUIDELINES_FUNNEL_PLAN.md §6.5).
//
// WRITE-ONLY by design (§6.3): the `leads/` path is not publicly readable
// (PII), so this never reads back. We use a single update() with the subject
// stored as `subjects/<key> = true` — idempotent and needs no prior read,
// which is exactly what write-only Firebase rules permit.

import { getApps, getApp, initializeApp } from "firebase/app";
import { getDatabase, ref, update, serverTimestamp } from "firebase/database";
import { firebaseConfig, processBDNumbers } from "@/config/utils";
import { getAttribution } from "@/config/attribution";

// Reuse the app main.js already initialized; init defensively otherwise.
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const db = getDatabase(app);

const LEAD_SOURCE = "guideline_pdf_optin";

// ── CAPI dedup/match helpers ──────────────────────────────────────────────
// The browser pixel Lead and the server-side (Conversions API) Lead are
// collapsed by Meta when they share an event_id. We mint it here, store it on
// the lead record (so the Cloud Function can reuse it), and return it so the
// caller can pass the SAME id to fbq's { eventID }. We also stash the Meta
// first-party cookies + user-agent the server can't otherwise see.

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name + "=([^;]+)")
  );
  return match ? decodeURIComponent(match[1]) : null;
};

// Meta's click id: prefer the _fbc cookie; otherwise synthesize from fbclid
// (format: fb.<subdomainIndex>.<creationTimeMs>.<fbclid>).
const resolveFbc = (fbclid) => {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
  return null;
};

const newEventId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `lead_${Date.now()}_${Math.random().toString(36).slice(2)}`;

/**
 * Validate + normalize a Bangladeshi phone number.
 * Returns the formatted 11-digit number, or null if invalid.
 */
export const normalizePhone = (raw) => {
  if (!raw) return null;
  const formatted = processBDNumbers(raw);
  return formatted === "Invalid BD phone number" ? null : formatted;
};

/**
 * Persist a guideline opt-in lead, deduped by phone.
 * @param {Object} args
 * @param {string} args.phone   raw phone input
 * @param {string} args.subject guideline key (bangla | english | bangladesh | ict)
 * @param {string} args.mode    entry mode at capture time (lead | member)
 * @returns {Promise<{ok: boolean, phone?: string, error?: string}>}
 */
export const saveLead = async ({ phone, subject, mode = "lead" }) => {
  const number = normalizePhone(phone);
  if (!number) return { ok: false, error: "invalid_phone" };

  // Single write, no read: subject stored as a child flag so repeat opt-ins
  // for different subjects accumulate (subjects/bangla=true, subjects/ict=true)
  // and the same phone always maps to one node — dedupe without reading back.
  const attribution = getAttribution();
  const eventId = newEventId();
  const capi = {
    eventId,
    fbp: readCookie("_fbp") || null,
    fbc: resolveFbc(attribution.fbclid) || null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    eventSourceUrl: typeof window !== "undefined" ? window.location.href : null,
  };
  const updates = {
    phone: number,
    source: LEAD_SOURCE,
    mode,
    attribution,
    capi,
    lastTs: serverTimestamp(),
  };
  if (subject) updates[`subjects/${subject}`] = true;

  try {
    await update(ref(db, `leads/${number}`), updates);
    return { ok: true, phone: number, eventId };
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      console.error("[saveLead] failed:", e);
    }
    return { ok: false, error: "write_failed" };
  }
};